import { Accordion, AccordionDetails, FormHelperText, Typography } from '@mui/material'
import { Divider, Grid, IconButton, InputAdornment, Stack, TextField, Tooltip } from '@vapor/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CoDepositFragment, DocumentType, FiscalType } from 'src/api/api.generated'
import { CheckoutDepositsSchema } from '../../checkout-document-form.schema'
import { CoData } from '../co-query-wrapper'
import { TFunction } from 'i18next'
import { FormItem } from 'hic-components'
import HicAmountField from '../../../../../../components/hic-amount-field/hic-amount-field'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { AccordionSummary } from '../accordion-summary'
import { CoAmountsError } from '../co-amounts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRotateLeft, faClose } from '@fortawesome/free-solid-svg-icons'
import { HICTitleSmall } from '../../../../../../components/typography/title-small'
import { HICError } from '../../../../../../components/typography/error-text'
import { v4 as uuidv4 } from 'uuid'

export type CoDepositsProps = {
  onChange: (value: CheckoutDepositsSchema) => void
  queryData: CoData
  error?: CoAmountsError
  disabled?: boolean
}

export function CoDeposits({ onChange, queryData, error, disabled }: CoDepositsProps) {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()

  const [localValue, setLocalValue] = useState<LDepositAssigned[]>([])

  useEffect(() => setLocalValue(queryDataToLocalValue(queryData, t)), [queryData, t])

  useEffect(() => onChange(localValue), [localValue])

  const updateDepositAmount = useCallback(
    (depositAssignedId: string, amount: number | null) => {
      const index = localValue.findIndex((d) => d.depositAssignedId === depositAssignedId)
      if (index < 0) {
        return
      }
      const newDeposit = {
        ...localValue[index],
        usedAmount: amount,
      }

      const newDeposits = [...localValue]
      newDeposits[index] = newDeposit

      setLocalValue(newDeposits)
    },
    [localValue],
  )

  const errorMessage = useMemo(() => {
    if (error?.billsAmount?.message || error?.totalAmount?.message || error?.paymentAmount?.message) {
      return t('pms.booking.reservation.checkout_document.error.deposit_amount_gt_document_total')
    }
    return undefined
  }, [error, t])

  return (
    <>
      <Grid item xs={12} style={{ paddingTop: '0px' }}>
        <Accordion disableGutters elevation={0} square expanded={true}>
          <AccordionSummary sx={{ paddingRight: '0px', paddingLeft: '0px', '& .MuiAccordionSummary-content': { marginLeft: '0px' } }} expandIcon={<></>}>
            <HICTitleSmall>{t('pms.common.checkout_document.amounts.deposits')}</HICTitleSmall>
          </AccordionSummary>
          <AccordionDetails style={{ paddingRight: '0px', paddingLeft: '0px' }}>
            {errorMessage && (
              <Grid container spacing={1}>
                <Grid item xs={12} style={{ paddingTop: 0 }}>
                  <FormHelperText style={{ paddingTop: 0, marginTop: 0 }} error>
                    {errorMessage}
                  </FormHelperText>
                </Grid>
              </Grid>
            )}
            {localValue.length === 0 && <Typography>{t('pms.common.checkout_document.amounts.no_deposits')}</Typography>}
            {localValue.length > 0 && (
              <Grid container spacing={1}>
                {localValue.map((d) => (
                  <React.Fragment key={d.depositAssignedId}>
                    <Grid item xs={8}>
                      <FormItem fullWidth>
                        <TextField readOnly value={`${d.description} ${d.fiscalType !== 'NON_FISCAL' ? `- ${t('commons.vat')}: ${d.vatDescription}` : ``}`} />
                      </FormItem>
                    </Grid>
                    <Grid item xs={4}>
                      <Stack direction="row">
                        <FormItem //
                          fullWidth
                        >
                          <HicAmountField //
                            nonNegative
                            disabled={queryData.mode !== 'CREATE' || disabled}
                            currency={currency}
                            value={d.usedAmount}
                            onChange={(value) => updateDepositAmount(d.depositAssignedId, value)}
                            InputProps={
                              queryData.mode === 'CREATE' //
                                ? { endAdornment: <InputAdornment position="end">{`/ ${formatCurrency(d.depositAmount, currency)}`}</InputAdornment> }
                                : undefined
                            }
                            error={(d.usedAmount ?? 0) > d.maxAmount}
                          />
                        </FormItem>
                        {d.usedAmount !== null && d.usedAmount > 0 ? (
                          <IconButton
                            disabled={queryData.mode !== 'CREATE' || disabled}
                            sx={{ ml: 1 }}
                            onClick={() => {
                              updateDepositAmount(d.depositAssignedId, 0)
                            }}
                          >
                            <FontAwesomeIcon icon={faClose} />
                          </IconButton>
                        ) : (
                          <IconButton
                            disabled={queryData.mode !== 'CREATE' || disabled}
                            sx={{ ml: 1 }}
                            onClick={() => {
                              updateDepositAmount(d.depositAssignedId, d.maxAmount)
                            }}
                          >
                            <Tooltip title={t('commons.reset.tooltip')}>
                              <span>
                                <FontAwesomeIcon icon={faArrowRotateLeft} />
                              </span>
                            </Tooltip>
                          </IconButton>
                        )}
                      </Stack>
                    </Grid>

                    {(d.usedAmount ?? 0) > d.maxAmount ? (
                      <Grid item>
                        <HICError>{t('pms.accounting.documents.max_deposit_amount_warning', { maxAmount: formatCurrency(d.maxAmount, currency) })}</HICError>
                      </Grid>
                    ) : undefined}
                  </React.Fragment>
                ))}
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12} style={{ paddingTop: '0px' }}>
        <Divider light />
      </Grid>
    </>
  )
}

type LDepositAssigned = {
  depositAssignedId: string
  fiscalType: FiscalType
  description: string
  usedAmount: number | null
  maxAmount: number
  depositAmount: number
  vatPercentage: number
  vatDescription: string
  uuid: string
}

function queryDataToLocalValue(queryData: CoData, t: TFunction): LDepositAssigned[] {
  if (queryData.mode === 'CREATE') {
    const retrieveNonFiscalDeposits = (queryData: CoData) => {
      if (queryData.mode === 'CREATE') {
        const billAmount =
          queryData.bills.map((bill) => bill.productions?.map((p) => p.production.total * p.quota).reduce((prev, curr) => prev + curr, 0)).reduce((prev, curr) => (prev ?? 0) + (curr ?? 0), 0) ?? 0

        const das: LDepositAssigned[] = queryData.deposits
          .filter((d) => d.fiscalType === FiscalType.NON_FISCAL)
          .reduce((p: LDepositAssigned[], ndf) => {
            const das: LDepositAssigned[] = (ndf.depositAssigneds || []).map((da) => {
              const description = `${t('pms.booking.reservation.deposits.type.not_fiscal')} - ${ndf.numberExtended}`
              const vatPercentage = 0 //ndf.documentDetails?.[0]?.reference?.accountantCategory?.defaultVat?.value ?? 0
              return {
                uuid: uuidv4(), // id is required only to mantain order of documentPaymentDetails
                depositAssignedId: da.id,
                description: description,
                fiscalType: ndf.fiscalType,
                maxAmount: Math.min(da.amount, billAmount),
                usedAmount: 0,
                depositAmount: da.amount,
                vatDescription: '',
                vatPercentage: vatPercentage,
              }
            })
            return [...p, ...das]
          }, [])
        return das
      }
    }

    const retrieveFiscalDeposits = (queryData: CoData) => {
      if (queryData.mode === 'CREATE') {
        const das: LDepositAssigned[] = queryData.deposits
          .filter((d) => d.fiscalType !== FiscalType.NON_FISCAL)
          .reduce((p: LDepositAssigned[], d) => {
            const das: LDepositAssigned[] = (d.depositAssigneds || []).map((da) => {
              const description = `${t('pms.booking.reservation.deposits.type.fiscal')} - ${d.numberExtended}`

              //* Recupero il valore di iva del anticipo fiscale
              let fiscalDepositMax = 0
              let vatPercentage = 0
              let vatDescription = ''

              if (d.documentDetails.length > 0) {
                // Escludiamo eventuali righe di tipo *nota* dai depositi (queste righe potrebbero essere presenti su documenti importati da altri PMS)
                const validDetails = d.documentDetails.filter((r) => r.reference.accountantCategory)
                if (validDetails.length > 0 && validDetails[0].reference.accountantCategory) {
                  //* Per ogni produzione di ogni bill
                  queryData.bills.forEach((bill) => {
                    bill.productions?.forEach((p) => {
                      if (p.production.vat.value === (validDetails[0].vat?.value ?? validDetails[0].reference.accountantCategory?.defaultVat.value)) {
                        fiscalDepositMax += p.production.total * p.quota
                      }
                    })
                  })
                  vatPercentage = validDetails[0].vat?.value ?? validDetails[0].reference.accountantCategory?.defaultVat.value
                  vatDescription = validDetails[0].vat?.description ?? validDetails[0].reference.accountantCategory?.defaultVat.description
                }
              }

              return {
                uuid: uuidv4(), // id is required only to mantain order of documentPaymentDetails
                depositAssignedId: da.id,
                description: description,
                fiscalType: d.fiscalType,
                maxAmount: Math.min(fiscalDepositMax, da.amount),
                usedAmount: 0,
                depositAmount: da.amount,
                vatPercentage,
                vatDescription,
              }
            })
            return [...p, ...das]
          }, [])
          .filter((d) => d.maxAmount > 0)
        return das
      }
    }

    const das: LDepositAssigned[] = [...(retrieveNonFiscalDeposits(queryData) ?? []), ...(retrieveFiscalDeposits(queryData) ?? [])]
    console.log('das', das)

    das.sort((a, b) => {
      const numberA = a.fiscalType === FiscalType.NON_FISCAL ? 1 : 2
      const numberB = b.fiscalType === FiscalType.NON_FISCAL ? 1 : 2
      return numberA - numberB
    })

    //* Mappa che definisce per ogni valore di IVA l'ammontare dei bill con quell'IVA
    const billsAmount = new Map<number, number>([])
    queryData.bills.forEach((bill) => {
      bill.productions?.forEach((prod) => {
        billsAmount.set(prod.production.vat.value, (billsAmount.get(prod.production.vat.value) ?? 0) + prod.production.total * prod.quota)
      })
    })

    for (let index = 0; index < das.length; index++) {
      const deposit = das[index]
      let key = undefined
      let billAmount = undefined
      if (deposit.fiscalType !== 'NON_FISCAL') {
        key = deposit.vatPercentage
        billAmount = billsAmount.get(key)
        //* Se ho un anticipo fiscale compatibile con l'iva dei bill
        if (billAmount) {
          //* Caso: uso tutto il deposito
          if (deposit.maxAmount <= billAmount) {
            billsAmount.set(key, billAmount - deposit.maxAmount)
            deposit.usedAmount = deposit.maxAmount
          } else {
            //* Caso: il deposito è più grande del utilizzabile
            billsAmount.set(key, 0)
            deposit.usedAmount = billAmount
          }
        }
      } else {
        // Se il deposito è non fiscale può essere applicato su titti gli addebiti indipendentemente dall'aliquota iva.
        for (const [key, value] of billsAmount.entries()) {
          if (deposit.maxAmount - (deposit.usedAmount ?? 0) <= value) {
            billsAmount.set(key, value - deposit.maxAmount)
            deposit.usedAmount = deposit.maxAmount
          } else {
            billsAmount.set(key, 0)
            deposit.usedAmount = (deposit.usedAmount ?? 0) + value
          }
        }
      }
    }

    return das
  } else {
    const deposits: CoDepositFragment[] = queryData.document.documentDetails.reduce((p: CoDepositFragment[], detail) => {
      if (detail.referenceDocument?.documentType === DocumentType.ADVANCE) {
        return [...p, detail.referenceDocument]
      } else {
        return p
      }
    }, [])

    const das = deposits.reduce((p: LDepositAssigned[], d) => {
      const das: LDepositAssigned[] = (d.depositAssigneds?.filter((da) => da.documentId && da.documentId === queryData.document.id) || []).map((da) => {
        const depositType = d.fiscalType === FiscalType.NON_FISCAL ? t('pms.booking.reservation.deposits.type.not_fiscal') : t('pms.booking.reservation.deposits.type.fiscal')

        const vatPercentage = d.documentDetails?.[0]?.vat?.value ?? d.documentDetails?.[0]?.reference?.accountantCategory?.defaultVat?.value ?? 0
        const vatDescription = d.documentDetails?.[0]?.vat?.description ?? d.documentDetails?.[0]?.reference?.accountantCategory?.defaultVat?.description ?? ''

        const description = `${depositType} - ${d.numberExtended}`
        return {
          uuid: uuidv4(), // id is required only to mantain order of documentPaymentDetails
          depositAssignedId: da.id,
          description: description,
          fiscalType: d.fiscalType,
          maxAmount: da.amount,
          usedAmount: da.amount,
          depositAmount: da.amount,
          vatPercentage,
          vatDescription,
        }
      })
      return [...p, ...das]
    }, [])

    das.sort((a, b) => {
      const numberA = a.fiscalType === FiscalType.NON_FISCAL ? 1 : 2
      const numberB = b.fiscalType === FiscalType.NON_FISCAL ? 1 : 2
      return numberA - numberB
    })

    return das
  }
}
